import type { CarReport } from '../api/types'
import {
  formatDate,
  formatEuro,
  formatNumber,
  formatPower,
  formatYear,
  isPast,
  vehicleName,
} from './format'

/**
 * The vehicle read back in a few sentences, assembled from fields the report already shows.
 *
 * Three jobs, and it is worth being clear which is which.
 *
 * 1. **A reader gets the car in one breath.** The report is a dozen dense cards; this is the
 *    paragraph that says what the thing actually is before the tables start.
 * 2. **The page gets prose.** A report page is otherwise a wall of labels and numbers, which is
 *    thin material for a search engine, and every one of them looks the same as every other.
 * 3. **The meta description gets something unique per plate.** See `meta.ts`, which builds a
 *    shorter line from the same fields rather than truncating this one.
 *
 * **It states, it does not judge.** Every clause traces to one field and none of them weighs
 * anything: "rijdt op benzine", never "zuinig". That is the same rule the whole report runs on, and
 * it is the reason a generated paragraph is allowed here at all. The pros-and-cons summary proposed
 * for the top of the report is a different feature with a different rule, and this is not a back
 * door to it.
 */

/** A run of text, marked when it is one of the values rather than the wording around it. */
export interface SummarySegment {
  text: string
  /** Set for the figures, which the component renders in bold so the paragraph can be skimmed. */
  value?: boolean
  /**
   * The card this run refers to, as an anchor, where there is one.
   *
   * A `SectionSpec` id from `ReportView`, the same as the quick-check tiles and the timeline use.
   * Renaming one there leaves a link that scrolls nowhere, silently.
   */
  href?: string
}

/**
 * The paragraph, as segments.
 *
 * Segments rather than a string because the figures are set in bold: a reader skimming should be
 * able to take the year, the price and the APK date off the paragraph without reading it, which is
 * the difference between prose people read and prose people skip.
 */
export function buildVehicleSummary(report: CarReport): SummarySegment[] {
  const sentences = [
    identitySentence(report),
    registrationSentence(report),
    figuresSentence(report),
    conditionSentence(report),
  ].filter(isPresent)

  return sentences.flatMap((sentence, index) => (index === 0 ? sentence : [text(' '), ...sentence]))
}

/**
 * Builds one clause, with every interpolated value marked, or drops it when a value is missing.
 *
 * A tagged template because the two things this has to do are the same thing: what gets emboldened
 * is exactly what came out of the report, and a clause whose figure is absent is exactly the clause
 * that must not be written. `phrase\`komt uit ${year}\`` is undefined when the RDW published no
 * year, and bold on the year when it did.
 */
function phrase(
  strings: TemplateStringsArray,
  ...values: (string | number | undefined)[]
): SummarySegment[] | undefined {
  if (values.some((value) => value === undefined || value === '')) {
    return undefined
  }

  return strings.flatMap((literal, index) => {
    const value = values[index]

    return [
      ...(literal ? [text(literal)] : []),
      ...(value === undefined ? [] : [{ text: String(value), value: true }]),
    ]
  })
}

function text(value: string): SummarySegment {
  return { text: value }
}

/** "Deze Peugeot Partner komt uit 2018 en kostte toen € 20.918." */
function identitySentence(report: CarReport): SummarySegment[] | undefined {
  const name = vehicleName(report.identity.brand, report.identity.tradeName)

  if (name === 'Onbekend voertuig') {
    return undefined
  }

  const clauses = [
    phrase`komt uit ${formatYear(report.registration.constructionYear)}`,
    phrase`kostte toen ${formatEuro(report.financial.cataloguePriceEuro)}`,
  ].filter(isPresent)

  if (clauses.length === 0) {
    return [text('Dit is een '), { text: name, value: true }, text('.')]
  }

  return [text('Deze '), { text: name, value: true }, text(' '), ...join(clauses), text('.')]
}

/**
 * "Deze geïmporteerde bedrijfsauto staat sinds 2022 op kenteken, rijdt op benzine en heeft een
 * maximumvermogen van 72 kW (98 pk) en een cilinderinhoud van 1.598 cc."
 */
function registrationSentence(report: CarReport): SummarySegment[] | undefined {
  const { identity, registration, powertrain } = report

  // "voertuigsoort" is registered in sentence case and reads as a shout mid-sentence otherwise.
  const kind = identity.vehicleKind?.toLowerCase() ?? 'auto'
  const fuels = powertrain.energySources?.map((source) => source.description).filter(isText) ?? []

  const clauses = [
    phrase`staat sinds ${registration.currentRegistrationSince?.slice(0, 4)} op kenteken`,
    fuels.length > 0
      ? [text('rijdt op '), { text: joinWords(fuels.map((fuel) => fuel.toLowerCase())), value: true }]
      : undefined,
    // Power and displacement share a verb. Two clauses each opening "heeft een" reads like a list
    // that forgot it was a sentence.
    engineClause(
      formatPower(powertrain.maximumPowerKw, powertrain.maximumPowerHp),
      formatNumber(powertrain.engineCapacityCc, 'cc'),
    ),
  ].filter(isPresent)

  if (clauses.length === 0) {
    return undefined
  }

  const subject = `Deze ${registration.isImported ? 'geïmporteerde ' : ''}${kind} `

  return [text(subject), ...join(clauses), text('.')]
}

function engineClause(power: string | undefined, capacity: string | undefined): SummarySegment[] | undefined {
  return (
    phrase`heeft een maximumvermogen van ${power} en een cilinderinhoud van ${capacity}` ??
    phrase`heeft een maximumvermogen van ${power}` ??
    phrase`heeft een cilinderinhoud van ${capacity}`
  )
}

/** "Het gemiddelde verbruik is 6,5 liter per 100 km, het leeggewicht is 1.259 kg en de APK is geldig tot 09-07-2027." */
function figuresSentence(report: CarReport): SummarySegment[] | undefined {
  const { powertrain, technical, inspection } = report

  // Litres before watt-hours. A plug-in hybrid publishes both, and the RDW lists electricity first,
  // so taking the first source with any figure at all would tell most readers a number they cannot
  // place. An electric car has no litres and falls through to its own figure.
  const sources = powertrain.energySources ?? []
  const litres = sources.find((entry) => entry.fuelConsumptionCombined !== undefined)?.fuelConsumptionCombined
  const watts = sources.find((entry) => entry.electricConsumptionWhPerKm !== undefined)?.electricConsumptionWhPerKm

  const consumption =
    phrase`het gemiddelde verbruik is ${litres && `${formatNumber(litres)} liter per 100 km`}` ??
    phrase`het gemiddelde verbruik is ${watts && `${formatNumber(watts)} Wh per km`}`

  const expiry = formatDate(inspection.apkExpiryDate)
  const expired = inspection.apkExpired ?? isPast(inspection.apkExpiryDate)

  const clauses = [
    consumption,
    phrase`het leeggewicht is ${formatNumber(technical.emptyMassKg, 'kg')}`,
    expired
      ? phrase`de APK is verlopen op ${expiry}`
      : phrase`de APK is geldig tot ${expiry}`,
  ].filter(isPresent)

  if (clauses.length === 0) {
    return undefined
  }

  return [...capitalise(join(clauses)), text('.')]
}

/**
 * "Bij de keuringen zijn 71 bevindingen genoteerd en er zijn 2 terugroepacties bekend."
 *
 * The condition sentence, which earns its place because the report leads with condition rather than
 * with specifications. Both figures link to the card that explains them.
 *
 * **Only notable when there is something to report.** A clean car gets no sentence rather than
 * "geen bevindingen en geen terugroepacties", for three reasons: silence keeps the paragraph short
 * on the majority of reports, "0 bevindingen" on a car nobody has inspected would be the false
 * all-clear the tiles are careful to avoid, and the empty answers are premium content that a free
 * paragraph should not be giving away once there is a paywall to give it away from.
 *
 * Top speed and gear count were considered here and left out. Top speed is a quick-check tile two
 * inches below, and the number of gears is trivia in a paragraph that has to stay readable.
 */
function conditionSentence(report: CarReport): SummarySegment[] | undefined {
  const clauses: SummarySegment[][] = []

  const found = report.inspection.totalDefectsFound
  const inspected = (report.inspection.history?.length ?? 0) > 0

  if (inspected && found > 0) {
    clauses.push([
      text('bij de keuringen zijn '),
      {
        text: `${formatNumber(found)} ${found === 1 ? 'bevinding' : 'bevindingen'}`,
        value: true,
        href: '#apk',
      },
      text(' genoteerd'),
    ])
  }

  const recalls = report.recalls.length

  if (recalls > 0) {
    clauses.push([
      text(recalls === 1 ? 'er is ' : 'er zijn '),
      {
        text: `${formatNumber(recalls)} ${recalls === 1 ? 'terugroepactie' : 'terugroepacties'}`,
        value: true,
        href: '#terugroepacties',
      },
      text(' bekend'),
    ])
  }

  return clauses.length > 0 ? [...capitalise(join(clauses)), text('.')] : undefined
}

/** Dutch enumeration across segment runs: "a", "a en b", "a, b en c". */
function join(clauses: SummarySegment[][]): SummarySegment[] {
  return clauses.flatMap((clause, index) => {
    if (index === 0) {
      return clause
    }

    return [text(index === clauses.length - 1 ? ' en ' : ', '), ...clause]
  })
}

/** The same, for plain words. */
function joinWords(words: string[]): string {
  if (words.length <= 1) {
    return words[0] ?? ''
  }

  return `${words.slice(0, -1).join(', ')} en ${words[words.length - 1]}`
}

function capitalise(segments: SummarySegment[]): SummarySegment[] {
  const [first, ...rest] = segments

  if (!first) {
    return segments
  }

  return [{ ...first, text: first.text.charAt(0).toUpperCase() + first.text.slice(1) }, ...rest]
}

function isPresent<T>(value: T | undefined): value is T {
  return value !== undefined
}

function isText(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}
